import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { PageTitle, Field, Chip, SearchBox, matchQ } from "../components/common";
import { Modal } from "../components/shared";

function DriverForm({ init, onClose, onSaved }) {
  const [f, setF] = useState({ name: init.name || "", employee_id: init.employee_id || "", active: init.active ?? true });
  const save = async () => {
    try {
      if (init.id) await api.patch(`/my/drivers/${init.id}`, f);
      else await api.post("/my/drivers", f);
      toast.success("Chauffeur enregistré");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Modal title={init.id ? "Modifier le chauffeur" : "Nouveau chauffeur"} onClose={onClose} testid="my-driver-form">
      <div className="space-y-3">
        <Field label="Nom du chauffeur"><input className="tl-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="my-driver-name" /></Field>
        <Field label="ID chauffeur (Employee ID Diad)"><input className="tl-input tl-mono uppercase" value={f.employee_id} onChange={(e) => setF({ ...f, employee_id: e.target.value })} data-testid="my-driver-employee-id" /></Field>
        <label className="flex gap-2 items-center text-sm"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="my-driver-active" /> Actif</label>
        <button className="tl-btn tl-btn-primary w-full" onClick={save} data-testid="my-driver-save">Enregistrer</button>
      </div>
    </Modal>
  );
}

function AccessForm({ driver, onClose, onSaved }) {
  const [f, setF] = useState({ email: driver.account?.email || "", password: "", active: driver.account?.active ?? true });
  const save = async () => {
    try {
      await api.post(`/my/drivers/${driver.id}/access`, f);
      toast.success("Accès chauffeur enregistré");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Modal title={`Accès de ${driver.name}`} onClose={onClose} testid="driver-access-form">
      <div className="space-y-3">
        <p className="text-sm text-slate-600">Le chauffeur pourra se connecter et encoder ses propres journées. Il ne voit aucun tarif.</p>
        <Field label="E-mail de connexion"><input type="email" className="tl-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="driver-access-email" /></Field>
        <Field label={driver.account ? "Nouveau mot de passe (vide = inchangé)" : "Mot de passe (8 caractères min.)"}><input type="password" className="tl-input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="driver-access-password" /></Field>
        <label className="flex gap-2 items-center text-sm"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="driver-access-active" /> Accès actif</label>
        <button className="tl-btn tl-btn-primary w-full" onClick={save} data-testid="driver-access-save">Enregistrer l'accès</button>
      </div>
    </Modal>
  );
}

export default function MyDrivers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [access, setAccess] = useState(null);
  const load = () => api.get("/my/drivers").then((r) => setRows(r.data));
  useEffect(() => {
    load();
  }, []);
  const done = () => { setEdit(null); setAccess(null); load(); };
  return (
    <div>
      <PageTitle title="Mes chauffeurs" sub="Créez vos chauffeurs et donnez-leur un accès à leurs encodages. Sans activité pendant 1 an, un chauffeur est retiré des listes (historique conservé)." />
      <div className="flex flex-wrap gap-2 mb-3">
        <button className="tl-btn tl-btn-primary" onClick={() => setEdit({})} data-testid="add-my-driver-button"><Plus size={16} /> Nouveau chauffeur</button>
        <SearchBox value={q} onChange={setQ} testid="my-drivers-search" />
      </div>
      <div className="tl-card overflow-x-auto">
        <table className="tl-table" data-testid="my-drivers-table">
          <thead><tr><th>Nom</th><th>ID chauffeur</th><th>État</th><th>Accès</th><th /></tr></thead>
          <tbody>
            {rows.filter((d) => matchQ(q, d.name, d.employee_id, d.account?.email)).map((d) => (
              <tr key={d.id} data-testid={`my-driver-${d.id}`}>
                <td>{d.name}</td>
                <td className="tl-mono">{d.employee_id}</td>
                <td>{d.active ? <Chip tone="green">actif</Chip> : <Chip>inactif</Chip>}</td>
                <td className="text-xs">{d.account ? `${d.account.email}${d.account.active ? "" : " (désactivé)"}` : "—"}</td>
                <td className="flex gap-1">
                  <button className="tl-btn tl-btn-ghost tl-btn-sm" onClick={() => setEdit(d)} data-testid={`edit-my-driver-${d.id}`}>Modifier</button>
                  <button className="tl-btn tl-btn-dark tl-btn-sm" onClick={() => setAccess(d)} data-testid={`access-my-driver-${d.id}`}>{d.account ? "Gérer l'accès" : "Donner accès"}</button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={5} className="text-slate-500">Aucun chauffeur.</td></tr>}
          </tbody>
        </table>
      </div>
      {edit && <DriverForm init={edit} onClose={() => setEdit(null)} onSaved={done} />}
      {access && <AccessForm driver={access} onClose={() => setAccess(null)} onSaved={done} />}
    </div>
  );
}
